import React, { useCallback, useEffect, useRef, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Alert, Box, Button, Tab, Tabs, Typography } from "@mui/material";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router";

import { ApplicationEndpoints } from "@generated/application";
import { ServicesTab } from "src/domains/application/components/ServicesTab";
import {
  useStackBilling,
  useOrgBilling,
} from "src/domains/application/hooks/useBilling";
import { usePipelineStatus } from "src/domains/application/hooks/useDeploymentEvents";
import { StackRealtimeProvider } from "src/domains/application/hooks/useStackRealtime";
import { useStackStatus } from "src/domains/application/hooks/useStackStatus";
import { Application, StackStatus } from "src/domains/application/types";
import { ORGANIZATIONS, PROJECTS } from "src/platform/constants/routes";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { usePollingApi } from "src/platform/hooks/usePollingApi";
import { useTabParam } from "src/platform/hooks/useTabParam";

import {
  ConfigurationTab,
  DeleteDialog,
  EventsTab,
  ManifestPreviewTab,
  PipelineTab,
  SecretsTab,
} from "./components";
import { useEnvironments } from "./components/pipelineDataHooks";
import {
  SummarySkeleton,
  PipelineSkeleton,
  ServicesSkeleton,
  SecretsSkeleton,
  CostsSkeleton,
  GenericSkeleton,
} from "./components/skeletons";
import { StackPageHeader } from "./components/StackPageHeader";
import { SummaryTab } from "./components/SummaryTab";
import { CostsTab } from "./CostsTab";
import { useStackActions } from "./hooks/useStackActions";
import { useStackValidation } from "./hooks/useStackValidation";
import { StackMetrics, StackTab } from "./types";

// ── Tab labels and order (text-only, no icons — matching mockup) ─────────────

const TAB_LABELS: Record<StackTab, string> = {
  overview: "Summary",
  pipeline: "Pipeline",
  services: "Services",
  events: "Events",
  configuration: "Settings",
  secrets: "Secrets",
  manifest: "Manifest",
  costs: "Costs",
};

const ALL_TABS: StackTab[] = [
  "overview",
  "pipeline",
  "services",
  "events",
  "configuration",
  "secrets",
  "manifest",
  "costs",
];

// ── Tab counter badge ──────────────────────────────────────────────────────────

function TabCounter({ count }: { count: number }): React.ReactElement {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 18,
        height: 18,
        borderRadius: "9px",
        bgcolor: "rgba(148,163,184,0.15)",
        fontSize: 11,
        fontWeight: 600,
        color: "text.secondary",
        ml: 0.5,
        px: 0.5,
      }}
    >
      {count}
    </Box>
  );
}

// ── Skeleton picker ───────────────────────────────────────────────────────────

function tabSkeleton(tab: StackTab): React.ReactElement {
  switch (tab) {
    case "overview":
      return <SummarySkeleton />;
    case "pipeline":
      return <PipelineSkeleton />;
    case "services":
      return <ServicesSkeleton />;
    case "secrets":
      return <SecretsSkeleton />;
    case "costs":
      return <CostsSkeleton />;
    default:
      return <GenericSkeleton />;
  }
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function StackDetailsPage() {
  const { orgAlias, projectAlias, sysAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
    sysAlias: string;
  }>();
  const nav = useAppNavigation();
  const { tab, setTab } = useTabParam<StackTab>("overview");

  const [system, setSystem] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveGeneralLoading, setSaveGeneralLoading] = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!sysAlias) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION.replace(":id", sysAlias)}`,
        {
          withCredentials: true,
        },
      );
      setSystem(res.data as Application);
    } catch {
      setError("Stack not found.");
    } finally {
      setLoading(false);
    }
  }, [sysAlias]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Metrics polled every 30s ────────────────────────────────────────────────
  const metricsUrl = sysAlias
    ? `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION_METRICS.replace(":id", sysAlias)}`
    : null;
  const { data: metrics } = usePollingApi<StackMetrics>(metricsUrl, 30_000);

  // ── WebSocket status updates ────────────────────────────────────────────────
  const { status: wsStatus, deploymentUrl: wsDeploymentUrl } =
    useStackStatus(sysAlias);

  useEffect(() => {
    if (wsStatus) {
      setSystem((s) => (s ? { ...s, status: wsStatus as StackStatus } : s));
    }
  }, [wsStatus]);

  // Re-fetch on vcs_provisioned transition
  const prevWsStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      wsStatus === StackStatus.VCS_PROVISIONED &&
      prevWsStatusRef.current !== StackStatus.VCS_PROVISIONED
    ) {
      void load();
    }
    prevWsStatusRef.current = wsStatus;
  }, [wsStatus, load]);

  // Re-fetch on running transition
  const prevRunningRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      wsStatus === StackStatus.RUNNING &&
      prevRunningRef.current !== StackStatus.RUNNING
    ) {
      void load();
    }
    prevRunningRef.current = wsStatus;
  }, [wsStatus, load]);

  useEffect(() => {
    if (wsDeploymentUrl) {
      setSystem((s) => (s ? { ...s, deploymentUrl: wsDeploymentUrl } : s));
    }
  }, [wsDeploymentUrl]);

  // ── Billing ─────────────────────────────────────────────────────────────────
  const { data: billingData, loading: billingLoading } = useStackBilling(
    sysAlias ?? "",
  );
  const { data: orgBillingData } = useOrgBilling(orgAlias ?? "");

  // ── Pipeline status for failure detection ───────────────────────────────────
  const { steps: pipelineSteps } = usePipelineStatus(sysAlias);

  // ── Hooks ───────────────────────────────────────────────────────────────────
  const actions = useStackActions({
    sysAlias,
    orgAlias,
    projectAlias,
    system,
    setSystem,
  });

  const handleSaveGeneral = useCallback(
    async ({ name, pipelineMode }: { name: string; pipelineMode: string }) => {
      if (!sysAlias) return;
      setSaveGeneralLoading(true);
      try {
        const response = await axios.patch(
          `${apiBaseUrls.BACKEND}${ApplicationEndpoints.UPDATE_APPLICATION.replace(":id", sysAlias)}`,
          {
            name: name.trim(),
            pipelineMode,
          },
          { withCredentials: true },
        );
        setSystem(response.data as Application);
      } finally {
        setSaveGeneralLoading(false);
      }
    },
    [sysAlias],
  );

  const { environments } = useEnvironments(orgAlias ?? "", projectAlias ?? "");
  const [targetEnvironmentSlug, setTargetEnvironmentSlug] = useState("");
  const targetEnvironments = React.useMemo(
    () =>
      environments
        .filter((environment) =>
          ["dev", "staging", "production"].includes(environment.slug),
        )
        .map((environment) => ({
          slug: environment.slug,
          name: environment.name,
        })),
    [environments],
  );
  const promotionTargets = React.useMemo(() => {
    if (!system || !environments.length) return [];
    return environments
      .filter(
        (env) =>
          env.id !== system.environmentId &&
          ["dev", "staging", "production"].includes(env.slug),
      )
      .map((env) => ({ slug: env.slug, name: env.name }));
  }, [environments, system]);

  const validation = useStackValidation(
    system,
    orgBillingData?.quotaExhausted,
    pipelineSteps,
  );

  // ── Auto-navigate to Pipeline on FAILED / PROVISIONING_FAILED ───────────────
  const autoRedirectedRef = useRef(false);
  const handleTabChange = (_: React.SyntheticEvent, v: StackTab) => {
    setTab(v);
  };

  useEffect(() => {
    if (autoRedirectedRef.current) return;
    if (!system) return;
    if (
      system.status === StackStatus.FAILED ||
      system.status === StackStatus.PROVISIONING_FAILED
    ) {
      autoRedirectedRef.current = true;
      setTab("pipeline");
    }
  }, [system?.status, setTab]);

  // ── Loading state: per-tab skeleton ─────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        {/* Skeleton breadcrumbs — matches loaded page's breadcrumb block */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 240,
              height: 18,
              borderRadius: 1,
              bgcolor: "rgba(148,163,184,0.15)",
              animation: "pulse 1.4s ease-in-out infinite",
            }}
          />
        </Box>

        {/* Skeleton page header — matches StackPageHeader structure */}
        <Box sx={{ mb: 3 }}>
          {/* Title row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                width: 180,
                height: 28,
                borderRadius: 1,
                bgcolor: "rgba(148,163,184,0.15)",
                animation: "pulse 1.4s ease-in-out infinite",
              }}
            />
            <Box
              sx={{
                width: 70,
                height: 22,
                borderRadius: "12px",
                bgcolor: "rgba(148,163,184,0.15)",
              }}
            />
            <Box
              sx={{
                width: 90,
                height: 22,
                borderRadius: "12px",
                bgcolor: "rgba(148,163,184,0.15)",
              }}
            />
          </Box>
          {/* Action buttons row */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box
              sx={{
                width: 110,
                height: 34,
                borderRadius: "8px",
                bgcolor: "rgba(148,163,184,0.15)",
              }}
            />
            <Box
              sx={{
                width: 90,
                height: 34,
                borderRadius: "8px",
                bgcolor: "rgba(148,163,184,0.15)",
              }}
            />
          </Box>
        </Box>

        {/* Skeleton tab bar — matches loaded page's Tabs component */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            mb: 3,
          }}
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <Box
              key={i}
              sx={{
                height: 16,
                width: 70,
                borderRadius: 1,
                bgcolor: "rgba(148,163,184,0.15)",
              }}
            />
          ))}
        </Box>

        {/* Per-tab skeleton */}
        {tabSkeleton(tab)}
      </Box>
    );
  }

  // ── Error / not-found state ──────────────────────────────────────────────────
  if (error || !system) {
    return (
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            color: "text.secondary",
          }}
        >
          <Typography
            sx={{ fontSize: 16, fontWeight: 600, color: "text.primary", mb: 1 }}
          >
            Stack not found
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: "text.secondary",
              mb: 2,
              maxWidth: 420,
              mx: "auto",
            }}
          >
            {error ??
              "This stack may have been deleted, or you may not have access to it."}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void load()}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            Try again
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Status alert (replaces StackStatusBar) ───────────────────────────────────
  const showStatusAlert =
    (actions.actionError || actions.deployMessage) && !loading;
  const statusAlertSeverity: "error" | "info" | "warning" = actions.actionError
    ? "error"
    : actions.deployMessage
      ? "info"
      : "warning";
  const statusAlertMessage = actions.actionError ?? actions.deployMessage ?? "";

  const pipelineRunCount = 0;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <RouterLink
            to={
              orgAlias
                ? ORGANIZATIONS.DETAILS.getPath(orgAlias)
                : ORGANIZATIONS.ROOT.path
            }
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {orgAlias}
          </RouterLink>
          {" / "}
          <RouterLink
            to={orgAlias ? PROJECTS.LIST.getPath(orgAlias) : "#"}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            Projects
          </RouterLink>
          {" / "}
          <RouterLink
            to={
              orgAlias && projectAlias
                ? PROJECTS.DETAILS.getPath(orgAlias, projectAlias)
                : "#"
            }
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {projectAlias}
          </RouterLink>
          {" / "}
          <Typography component="span" variant="body2" color="text.primary">
            {system.name}
          </Typography>
        </Typography>
      </Box>

      {/* Page header */}
      <StackPageHeader
        system={system}
        onRunPipeline={() => void actions.buildAndDeploy(targetEnvironmentSlug)}
        onDeploy={() => void actions.deploy(targetEnvironmentSlug)}
        onPromote={(slug) => void actions.promote(slug)}
        onEdit={() => {
          if (system.stackConfig?._adopted) {
            nav.goToStackServices(orgAlias!, projectAlias!, sysAlias!);
          } else {
            nav.goToStackEdit(orgAlias!, projectAlias!, sysAlias!);
          }
        }}
        onDelete={() => actions.setDeleteDialogOpen(true)}
        actionLoading={actions.actionLoading}
        canDeploy={validation.canDeploy}
        promotionTargets={promotionTargets}
        targetEnvironments={targetEnvironments}
        selectedTargetEnvironment={targetEnvironmentSlug}
        onTargetEnvironmentChange={setTargetEnvironmentSlug}
        isAdopted={Boolean(system.stackConfig?._adopted)}
      />

      {/* Status alert (single, dismissible) */}
      {showStatusAlert && (
        <Alert
          severity={statusAlertSeverity}
          sx={{ mb: 2, borderRadius: "8px" }}
          onClose={() => {
            if (actions.actionError) actions.clearActionError();
            if (actions.deployMessage) actions.clearDeployMessage();
          }}
        >
          {statusAlertMessage}
        </Alert>
      )}

      {/* Tab bar — text-only, no icons, matching mockup */}
      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 40,
          "& .MuiTabs-indicator": {
            backgroundColor: "primary.main",
            height: 2,
          },
          "& .MuiTab-root": {
            minHeight: 40,
            py: 1.25,
            px: 2,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            color: "text.secondary",
          },
          "& .Mui-selected": {
            color: "text.primary !important",
          },
        }}
      >
        {ALL_TABS.map((t) => (
          <Tab
            key={t}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {TAB_LABELS[t]}
                {t === "pipeline" && pipelineRunCount > 0 && (
                  <TabCounter count={pipelineRunCount} />
                )}
              </Box>
            }
            value={t}
          />
        ))}
      </Tabs>

      {/* Tab content */}
      <StackRealtimeProvider
        alias={sysAlias!}
        orgAlias={orgAlias}
        vcsRepoUrl={system.vcsRepoUrl}
      >
        {tab === "overview" && (
          <SummaryTab
            system={system}
            metrics={metrics}
            metricsLoading={false}
            onTabChange={(t) => setTab(t as StackTab)}
            environments={environments.map((e) => ({
              slug: e.slug,
              name: e.name,
            }))}
            onPromote={(slug) => void actions.promote(slug)}
            billingData={billingData}
            orgAlias={orgAlias}
          />
        )}
        {tab === "pipeline" && (
          <PipelineTab
            alias={sysAlias!}
            templateSlug={system.templateSlug ?? undefined}
            pipelineMode={system.pipelineMode}
            orgAlias={orgAlias}
            vcsRepoUrl={system.vcsRepoUrl}
            status={system.status}
            projectAlias={projectAlias}
            targetEnvironmentSlug={targetEnvironmentSlug}
            onProvisionVcs={() => void actions.provisionVcs()}
            onPromote={(slug) => void actions.promote(slug)}
          />
        )}
        {tab === "services" && (
          <ServicesTab
            alias={sysAlias!}
            onRunPipeline={() =>
              void actions.buildAndDeploy(targetEnvironmentSlug)
            }
          />
        )}
        {tab === "events" && <EventsTab alias={sysAlias!} />}
        {tab === "configuration" && (
          <ConfigurationTab
            system={system}
            onDeleteProtectionChange={actions.deleteProtectionChange}
            deleteProtectionLoading={actions.deleteProtectionLoading}
            onAutoDeployChange={actions.autoDeployChange}
            autoDeployLoading={actions.autoDeployLoading}
            onRetryProvisioning={actions.retryProvisioning}
            retryProvisioningLoading={actions.retryProvisioningLoading}
            onTabChange={(t) => setTab(t)}
            onSaveGeneral={handleSaveGeneral}
            saveGeneralLoading={saveGeneralLoading}
          />
        )}
        {tab === "secrets" && (
          <SecretsTab
            system={system}
            onRedeploy={() =>
              void actions.buildAndDeploy(targetEnvironmentSlug)
            }
          />
        )}
        {tab === "manifest" && <ManifestPreviewTab system={system} />}
        {tab === "costs" && (
          <CostsTab
            alias={sysAlias!}
            data={billingData}
            loading={billingLoading}
            error={null}
          />
        )}
      </StackRealtimeProvider>

      <Box sx={{ mt: 4 }}>
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to={
            orgAlias && projectAlias
              ? PROJECTS.DETAILS.getPath(orgAlias, projectAlias)
              : "#"
          }
          sx={{ textTransform: "none" }}
        >
          Back to project
        </Button>
      </Box>

      <DeleteDialog
        open={actions.deleteDialogOpen}
        systemName={system.name}
        deleteProtection={system.deleteProtection ?? false}
        onClose={() => actions.setDeleteDialogOpen(false)}
        onSoftDelete={actions.softDelete}
        onHardDelete={actions.hardDelete}
        loading={actions.actionLoading}
      />
    </Box>
  );
}
